import { z } from 'zod';

/**
 * Anmeldungs-Status Enum.
 */
export const RegistrationStatusSchema = z.enum(['confirmed', 'cancelled', 'waitlisted', 'no_show']);

/** TypeScript Type für Anmeldungs-Status */
export type RegistrationStatus = z.infer<typeof RegistrationStatusSchema>;

/**
 * Anmeldungs-Quelle Enum.
 */
export const RegistrationSourceSchema = z.enum(['web', 'discord', 'google_form', 'manual']);

/** TypeScript Type für Anmeldungs-Quelle */
export type RegistrationSource = z.infer<typeof RegistrationSourceSchema>;

/**
 * Zod Schema für EventRegistration — Runtime-Validierung + Type-Inference.
 * Universelle Event-Anmeldung für alle Kategorien.
 */
export const EventRegistrationSchema = z.object({
  id: z.string().cuid(),
  eventId: z.string().cuid(),
  userId: z.string(),
  status: RegistrationStatusSchema.default('confirmed'),
  source: RegistrationSourceSchema.default('web'),
  companions: z.number().int().nonnegative().default(0),
  notes: z.string().nullable().optional(),
  registeredAt: z.coerce.date(),
  cancelledAt: z.coerce.date().nullable().optional(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type EventRegistration = z.infer<typeof EventRegistrationSchema>;

/**
 * Schema für EventRegistration-Erstellung.
 */
export const CreateEventRegistrationSchema = EventRegistrationSchema.pick({
  eventId: true,
  userId: true,
  status: true,
  source: true,
  companions: true,
  notes: true,
});

/** TypeScript Type für EventRegistration-Erstellung */
export type CreateEventRegistration = z.infer<typeof CreateEventRegistrationSchema>;

/**
 * Schema für EventRegistration-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateEventRegistrationSchema = EventRegistrationSchema.pick({
  status: true,
  companions: true,
  notes: true,
  cancelledAt: true,
}).partial();

/** TypeScript Type für EventRegistration-Update */
export type UpdateEventRegistration = z.infer<typeof UpdateEventRegistrationSchema>;

/**
 * Schema für das Aktualisieren der Notizen einer Event-Anmeldung.
 * Wird für PATCH /events/:eventId/registrations/:userId/notes genutzt.
 */
export const UpdateRegistrationNotesSchema = z.object({
  /** Interne Anmerkungen zur Anmeldung — null löscht die Notiz */
  notes: z.string().nullable(),
});

/** TypeScript Type für Notizen-Update einer Event-Anmeldung */
export type UpdateRegistrationNotes = z.infer<typeof UpdateRegistrationNotesSchema>;

/**
 * Ergebnis-Schema für eine öffentliche Form-Submission (Ticket-Anfrage).
 *
 * Kommuniziert dem Frontend ob die Anmeldung direkt bestätigt wurde
 * oder ob der User auf der Warteliste gelandet ist.
 */
export const FormSubmissionResponseSchema = z.object({
  /** Immer true bei erfolgreicher Verarbeitung */
  success: z.literal(true),
  /**
   * Anmelde-Status:
   * - `confirmed` — Platz ist reserviert, Bestätigungsmail wurde versendet
   * - `waitlisted` — Kein Platz verfügbar, User steht auf der Warteliste
   */
  status: z.enum(['confirmed', 'waitlisted']),
  /** Position auf der Warteliste (nur wenn status === 'waitlisted') */
  waitlistPosition: z.number().int().positive().optional(),
});

/** TypeScript Type für die Form-Submission-Response */
export type FormSubmissionResponse = z.infer<typeof FormSubmissionResponseSchema>;

/**
 * Öffentliche Event-Verfügbarkeit — ergänzende Felder für GET /public/events/:id.
 */
export const PublicEventAvailabilitySchema = z.object({
  /** Freie Plätze (null wenn kein Limit konfiguriert) */
  availableSpots: z.number().int().nonnegative().nullable(),
  /** Ob das Event ausgebucht ist */
  isFull: z.boolean(),
  /** Ob bereits Wartelisten-Einträge existieren */
  isWaitlistActive: z.boolean(),
});

/** TypeScript Type für öffentliche Event-Verfügbarkeit */
export type PublicEventAvailability = z.infer<typeof PublicEventAvailabilitySchema>;
